import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Download, Calendar, FileText, Users } from 'lucide-react';

export default function UpdatesPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const updates = [
    {
      id: 1,
      title: "EU Sugar Policy Reform: Implications for ACP Countries",
      excerpt: "Analysis of the latest EU sugar policy reforms and their potential impact on ACP sugar exports and market access.",
      date: "2025-01-15",
      category: "policy",
      type: "policy-brief",
      downloadUrl: "#",
      readTime: "8 min read"
    },
    {
      id: 2,
      title: "ACP Sugar Industries Group Annual Meeting 2025",
      excerpt: "Summary of key discussions and decisions from the Annual General Meeting held in Port Louis, Mauritius.",
      date: "2025-01-10",
      category: "news",
      type: "press-release",
      downloadUrl: "#",
      readTime: "5 min read"
    },
    {
      id: 3,
      title: "Climate Adaptation Strategies for Sugar Production",
      excerpt: "Comprehensive guide on climate-resilient sugar farming practices and technologies for member countries.",
      date: "2024-12-20",
      category: "research",
      type: "research-paper",
      downloadUrl: "#",
      readTime: "15 min read"
    },
    {
      id: 4,
      title: "Market Access Update: UK Trade Negotiations",
      excerpt: "Latest developments in UK-ACP trade negotiations and implications for sugar market access post-Brexit.",
      date: "2024-12-15",
      category: "trade",
      type: "market-update",
      downloadUrl: "#",
      readTime: "6 min read"
    },
    {
      id: 5,
      title: "Sustainable Sugar Initiative Launch",
      excerpt: "Launch of the multi-stakeholder Sustainable Sugar Initiative aimed at promoting environmental and social sustainability.",
      date: "2024-12-01",
      category: "sustainability",
      type: "press-release",
      downloadUrl: "#",
      readTime: "4 min read"
    },
    {
      id: 6,
      title: "Digital Agriculture in Sugar Production: Best Practices Guide",
      excerpt: "Comprehensive overview of digital technologies and their applications in modern sugar farming and processing.",
      date: "2024-11-25",
      category: "technology",
      type: "technical-guide",
      downloadUrl: "#",
      readTime: "12 min read"
    }
  ];

  const categories = [
    { id: 'all', label: 'All Updates' },
    { id: 'policy', label: 'Policy' },
    { id: 'trade', label: 'Trade' },
    { id: 'news', label: 'News' },
    { id: 'research', label: 'Research' },
    { id: 'sustainability', label: 'Sustainability' },
    { id: 'technology', label: 'Technology' }
  ];

  const filteredUpdates = selectedCategory === 'all' 
    ? updates 
    : updates.filter(update => update.category === selectedCategory);

  const getCategoryColor = (category: string) => {
    const colors = {
      policy: 'bg-blue-100 text-blue-800',
      trade: 'bg-green-100 text-green-800',
      news: 'bg-purple-100 text-purple-800',
      research: 'bg-orange-100 text-orange-800',
      sustainability: 'bg-emerald-100 text-emerald-800',
      technology: 'bg-indigo-100 text-indigo-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type: string) => {
    if (type.includes('press')) return <Users className="w-4 h-4" />;
    if (type.includes('research') || type.includes('guide')) return <FileText className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Updates & Publications</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay informed with the latest news, policy updates, research findings, and industry 
            insights from the ACP/LDC Sugar Industries Group.
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? "default" : "outline"}
              onClick={() => setSelectedCategory(category.id)}
              className={selectedCategory === category.id ? "bg-amber-600 hover:bg-amber-700 action" : "action"}
            >
              {category.label}
            </Button>
          ))}
        </div>

        {/* Updates Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {filteredUpdates.map((update) => (
            <Card key={update.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <Badge className={`${getCategoryColor(update.category)} !text-md !text-semibold`}>
                    {update.category.charAt(0).toUpperCase() + update.category.slice(1)}
                  </Badge>
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(update.date).toLocaleDateString('en-US', {  
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                </div>
                <CardTitle className="hover:text-amber-600 transition-colors cursor-pointer">
                  {update.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  {update.excerpt}
                </CardDescription>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      {getTypeIcon(update.type)}
                      <span className="ml-1">{update.type.replace('-', ' ')}</span>
                    </div>
                    <span>{update.readTime}</span>
                  </div>
                  <Button size="sm" variant="outline" className="flex items-center space-x-1">
                    <Download className="w-4 h-4" />
                    <span>Download PDF</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Newsletter Signup */}
        <section className="bg-amber-50 rounded-lg p-8 text-center" >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Stay Updated</h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Subscribe to our newsletter to receive the latest updates, policy briefs, and industry 
            insights directly in your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email address"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <Button className="bg-amber-600 hover:bg-amber-700">
              Subscribe
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            We respect your privacy and will never share your email address.
          </p>
        </section>
      </div>
    </div>
  );
}